import { z } from "zod"

import { InventoryHoldArea } from "../../constants"
import { inventoryDestinationFormSchema, inventoryShelvingFormSchema, validateRequiredShelving } from "../../schemas"

import { MAX_PALLETS_COUNT } from "./constants"

import { InventoryAdjustmentReason, InventoryAdjustmentType, ProductPackagingType } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

const MIN_QUANTITY = 0.01

const MAX_QUANTITY = 9_999_999.99

interface CreateQuantitySchemaProps {
  lotQuantity?: number | null
}

export const createAdjustmentNewQuantitySchema = ({
  lotQuantity,
}: CreateQuantitySchemaProps = {}): z.ZodEffects<z.ZodNumber> =>
  z
    .number()
    .min(0, "New UoM Qty must be a positive number") // AS400 constraint
    .max(MAX_QUANTITY, `New UoM Qty must be less than or equal to ${formatNumber(MAX_QUANTITY)}`) // AS400 constraint
    .refine((value) => isNil(lotQuantity) || value !== lotQuantity, {
      message: "New UoM Qty must be different from Lot UoM",
    })

export const createAdjustmentQuantityToMoveSchema = ({
  lotQuantity,
}: CreateQuantitySchemaProps = {}): z.ZodEffects<z.ZodNumber> =>
  z
    .number()
    .gt(0, "Qty to Move must be greater than 0")
    .max(MAX_QUANTITY, `Qty to Move must be less than or equal to ${formatNumber(MAX_QUANTITY)}`) // AS400 constraint
    .refine((value) => isNil(lotQuantity) || value <= lotQuantity, {
      message: "Qty to Move must be less than or equal to Lot UoM",
    })

export const adjustmentLaunchFormSchema = z.object({
  warehouseAreaId: z.string().min(1, "Warehouse is required"),
  adjustmentType: z.union([
    z.literal(InventoryAdjustmentType.AdjustQuantity),
    z.literal(InventoryAdjustmentType.SplitPallets),
    z.literal(InventoryAdjustmentType.CombinePallets),
    z.literal(InventoryAdjustmentType.LotBatchCorrection),
    z.literal(InventoryAdjustmentType.AddNewPallets),
  ]),
})

export type AdjustmentLaunchFormValues = z.infer<typeof adjustmentLaunchFormSchema>

export const adjustmentBaseFormSchema = z.object({
  warehouseAreaId: z.string().min(1, "Warehouse is required"),
  comment: z.string().max(50, "Comment must not exceed 50 characters").optional(),
  document: z.instanceof(File).optional(),
})

export type AdjustmentBaseFormValues = z.infer<typeof adjustmentBaseFormSchema>

export const quantityAdjustmentFormSchema = adjustmentBaseFormSchema.extend({
  adjustmentReason: z.nativeEnum(InventoryAdjustmentReason, { required_error: "Adjustment Reason is required" }),
})

export type QuantityAdjustmentFormValues = z.infer<typeof quantityAdjustmentFormSchema>

export const splitPalletsAdjustmentFormSchema = adjustmentBaseFormSchema
  .extend({
    holdArea: z.nativeEnum(InventoryHoldArea, { required_error: "Area is required" }),
    shelving: inventoryShelvingFormSchema,
  })
  .superRefine((data, ctx) => {
    if (data.holdArea !== InventoryHoldArea.Warehouse) return

    validateRequiredShelving(ctx, data.shelving)
  })

export type SplitPalletsAdjustmentFormValues = z.infer<typeof splitPalletsAdjustmentFormSchema>

export const combinePalletsAdjustmentFormSchema = adjustmentBaseFormSchema
  .extend({
    targetReferenceNumber: z.number().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.targetReferenceNumber === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetReferenceNumber"],
        message: "Target Reference ID is required",
      })
    }
  })

export type CombinePalletsAdjustmentFormValues = z.infer<typeof combinePalletsAdjustmentFormSchema>

const createAdjustmentLotFormSchema = (quantityLabel: string) =>
  z.object({
    lotNumber: z.string().min(1, "Lot # is required"),
    batchNumber: z.string(),
    quantityPerPackage: z
      .number()
      .min(MIN_QUANTITY, `Pkg UoM must be greater than or equal to ${formatNumber(MIN_QUANTITY)}`) // AS400 constraint
      .max(MAX_QUANTITY, `Pkg UoM must be less than or equal to ${formatNumber(MAX_QUANTITY)}`) // AS400 constraint
      .nullable()
      .refine((val): boolean => val !== null, { message: "Pkg UoM is required" }),
    packagingType: z.nativeEnum(ProductPackagingType).nullable(),
    unitCount: z
      .number()
      .int("Units per Lot must be an integer")
      .nullable()
      .refine((val): boolean => val !== null, { message: "Units per Lot is required" }),
    quantity: z
      .number()
      .min(MIN_QUANTITY, `${quantityLabel} must be greater than or equal to ${formatNumber(MIN_QUANTITY)}`) // AS400 constraint
      .max(MAX_QUANTITY, `${quantityLabel} must be less than or equal to ${formatNumber(MAX_QUANTITY)}`) // AS400 constraint
      .nullable()
      .refine((val): boolean => val !== null, { message: `${quantityLabel} is required` }),
  })

export type AdjustmentLotFormValues = z.infer<ReturnType<typeof createAdjustmentLotFormSchema>>

export const lotCorrectionAdjustmentNewLotFormSchema = createAdjustmentLotFormSchema("New UoM Qty").extend({
  rawInventoryId: z.string(),
})

export const lotCorrectionAdjustmentExistingLotFormSchema = z
  .object({
    rawInventoryId: z.string(),
    palletReferenceNumber: z.number(),
    originalLotNumber: z.string(),
    originalBatchNumber: z.string(),
    originalPackageSize: z.number().nullable(),
    originalQuantity: z.number().nullable(),
    lotNumber: z.string().min(1, "Lot # is required"),
    batchNumber: z.string(),
    quantity: z
      .number()
      .min(0, "New UoM Qty must be a positive number") // AS400 constraint
      .max(MAX_QUANTITY, `New UoM Qty must be less than or equal to ${formatNumber(MAX_QUANTITY)}`) // AS400 constraint
      .nullable()
      .refine((val): boolean => val !== null, { message: "New UoM Qty is required" }),
  })
  .superRefine((lot, ctx) => {
    if (isNil(lot.quantity) || isNil(lot.originalQuantity)) return

    const isLotRenamed = lot.lotNumber !== lot.originalLotNumber || lot.batchNumber !== lot.originalBatchNumber

    if (isLotRenamed && lot.quantity > lot.originalQuantity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["quantity"],
        message: "New UoM Qty cannot exceed Current Lot UoM",
      })
      return
    }

    if (
      isLotRenamed &&
      lot.quantity > 0 &&
      lot.quantity < lot.originalQuantity &&
      lot.originalPackageSize &&
      lot.quantity % lot.originalPackageSize !== 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["quantity"],
        message: "New Qty UoM must be a whole multiple of Pkg UoM. Partial packages are not allowed.",
      })
    }
  })

export type LotCorrectionAdjustmentExistingLotFormValues = z.infer<typeof lotCorrectionAdjustmentExistingLotFormSchema>

export type LotCorrectionAdjustmentNewLotFormValues = z.infer<typeof lotCorrectionAdjustmentNewLotFormSchema>

export const lotCorrectionAdjustmentFormSchema = adjustmentBaseFormSchema.extend({
  existingLots: z.array(lotCorrectionAdjustmentExistingLotFormSchema),
  newLots: z.array(lotCorrectionAdjustmentNewLotFormSchema),
})

export type LotCorrectionAdjustmentFormValues = z.infer<typeof lotCorrectionAdjustmentFormSchema>

export const addPalletsAdjustmentLotFormSchema = createAdjustmentLotFormSchema("Total Lot UoM")

export const addPalletsAdjustmentPalletRowFormSchema = z.object({
  quantityPerPallet: z.number().nullable(),
  palletCount: z
    .number({ required_error: "Pallet Qty is required" })
    .int("Pallet Qty must be an integer")
    .min(1, "Pallet Qty must be greater than 0")
    .max(MAX_PALLETS_COUNT, `Pallet Qty must be less than or equal to ${MAX_PALLETS_COUNT}`),
  lots: z.array(addPalletsAdjustmentLotFormSchema).min(1),
})

export const addPalletsAdjustmentFormSchema = adjustmentBaseFormSchema.extend({
  adjustmentReason: z.nativeEnum(InventoryAdjustmentReason, {
    required_error: "Adjustment Reason is required",
  }),
  palletRow: addPalletsAdjustmentPalletRowFormSchema,
  destinations: z.array(inventoryDestinationFormSchema),
})

export type AddPalletsAdjustmentLotFormValues = z.infer<typeof addPalletsAdjustmentLotFormSchema>

export type AddPalletsAdjustmentPalletRowFormValues = z.infer<typeof addPalletsAdjustmentPalletRowFormSchema>

export type AddPalletsAdjustmentFormValues = z.infer<typeof addPalletsAdjustmentFormSchema>
